import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { EmptyState, PageHeader } from "../../components/Common";
import { ConfirmButton } from "../../components/ConfirmButton";
import { api, formatApiError, formatIDR } from "../../lib/api";

const empty = { name: "", price_per_trip: 0, ticket_price: 0, description: "", active: true, audience: "both", category: "" };
const AUDIENCE = { both: "Driver & Vendor", driver: "Driver", vendor: "Vendor" };

function TourDialog({ tour, onClose, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (tour) setForm(tour.id ? tour : empty); }, [tour]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { name: form.name, price_per_trip: Number(form.price_per_trip) || 0, ticket_price: Number(form.ticket_price) || 0, description: form.description, active: form.active, audience: form.audience || "both", category: form.category || "" };
    try {
      if (tour.id) await api.put(`/tours/${tour.id}`, payload);
      else await api.post("/tours", payload);
      toast.success("Produk tour tersimpan");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!tour} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tour-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{tour?.id ? "Edit Tour" : "Tour Baru"}</DialogTitle><DialogDescription>Harga dipakai otomatis saat driver membuat report.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Nama Tour</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="tour-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Harga per Trip (Rp)</Label><Input type="number" min={0} className="font-num" value={form.price_per_trip} onChange={(e) => set("price_per_trip", e.target.value)} data-testid="tour-price-input" /></div>
            <div className="space-y-1.5"><Label>Harga Tiket / Orang (Rp)</Label><Input type="number" min={0} className="font-num" value={form.ticket_price} onChange={(e) => set("ticket_price", e.target.value)} data-testid="tour-ticket-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Keterangan (opsional)</Label><Input value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="tour-desc-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Untuk</Label>
              <select value={form.audience || "both"} onChange={(e) => set("audience", e.target.value)} className="h-11 sm:h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="tour-audience-select">
                {Object.entries(AUDIENCE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label>Kategori (opsional)</Label><Input value={form.category || ""} onChange={(e) => set("category", e.target.value)} placeholder="Contoh: Snorkeling, Ubud" data-testid="tour-category-input" /></div>
          </div>
          <label className="flex items-center justify-between rounded-lg border p-3"><span className="text-sm font-medium">Aktif (bisa dipilih driver)</span><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="tour-active-switch" /></label>
          <Button type="submit" disabled={saving} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700" data-testid="tour-submit-btn">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Simpan</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AquaPriceCard() {
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setPrice(r.data.aqua_price)); }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", { aqua_price: Number(price) || 0 }); toast.success("Harga Aqua tersimpan"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="rise rounded-2xl border border-slate-200 bg-white p-5 mb-6 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="aqua-price-card">
      <div className="flex-1">
        <p className="font-heading font-semibold text-slate-900">Service Aqua</p>
        <p className="text-sm text-slate-500">Harga per pax, dipakai saat driver mencentang Aqua di report trip.</p>
      </div>
      <div className="flex gap-2">
        <Input type="number" min={0} className="w-40 font-num" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="aqua-price-input" />
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="aqua-price-save-btn">Simpan</Button>
      </div>
    </div>
  );
}

export default function AdminTours() {
  const [tours, setTours] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/tours").then((r) => setTours(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (t) => {
    try { await api.delete(`/tours/${t.id}`); toast.success("Tour dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Master Data" title="Produk Tour">
        <Button onClick={() => setEditing({})} className="h-10 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="add-tour-btn"><Plus className="h-4 w-4 mr-1" />Tambah Tour</Button>
      </PageHeader>
      <AquaPriceCard />
      {tours.length ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="tour-list">
          {tours.map((t, i) => (
            <div key={t.id} className="rise rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-md transition-shadow" style={{ animationDelay: `${i * 40}ms` }} data-testid={`tour-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-heading font-semibold text-lg text-slate-900">{t.name}</p>
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${t.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{t.active ? "Aktif" : "Nonaktif"}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-violet-50 text-violet-700" data-testid={`tour-audience-${t.id}`}>{AUDIENCE[t.audience || "both"]}</span>
                {t.category && <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600">{t.category}</span>}
              </div>
              {t.description && <p className="text-sm text-slate-500 mt-1">{t.description}</p>}
              <div className="grid grid-cols-2 gap-3 mt-5">
                <div><p className="text-[11px] uppercase tracking-wider text-slate-400">Per Trip</p><p className="font-num font-bold text-slate-900">{formatIDR(t.price_per_trip)}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-slate-400">Tiket / Orang</p><p className="font-num font-bold text-slate-900">{formatIDR(t.ticket_price)}</p></div>
              </div>
              <div className="flex gap-2 mt-5 pt-4 border-t border-slate-100">
                <Button variant="outline" size="sm" onClick={() => setEditing(t)} data-testid={`edit-tour-${t.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                <ConfirmButton title="Hapus tour?" description="Report trip lama tetap tersimpan. Untuk menyembunyikan saja, nonaktifkan tour." onConfirm={() => remove(t)} testId={`delete-tour-${t.id}`} confirmLabel="Hapus">
                  <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`delete-tour-${t.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Hapus</Button>
                </ConfirmButton>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState title="Belum ada produk tour" text="Tambahkan tour agar driver bisa membuat report." testId="empty-tours" />
      )}
      <TourDialog tour={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}
